import { inject, Injectable } from '@angular/core';
import { ApiValidationService } from '@rd/core/services/api-validation/api-validation.service';
import { DateService } from '@rd/core/services/date/date.service';
import { NydusNetworkApiDataService } from '@rd/core/services/nydus-network-api-data/nydus-network-api-data.service';
import { pointsSummaryResponseSchema } from '@rd/schemas/points-summary.schema';
import { Accrual, Tranche } from '@rd/types';
import { AccrualModel } from '@rd/types/accrual.model';
import { PointsSummaryModel } from '@rd/types/points-summary.model';
import { PointsSummaryResponse } from '@rd/types/points-summary.response';
import { PointsSummary } from '@rd/types/points-summary.type';
import { TrancheModel } from '@rd/types/tranche.model';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class PointsSummaryApiService {
  private nydusNetworkApiDataService = inject(NydusNetworkApiDataService);
  private dateService = inject(DateService);
  private apiValidationService = inject(ApiValidationService);

  getPointsSummary(): Observable<PointsSummary> {
    return this.nydusNetworkApiDataService
      .get<PointsSummaryResponse>('points_summary')
      .pipe(
        tap((pointsSummaryResponse) => {
          this.apiValidationService.validate<PointsSummaryResponse>({
            apiName: 'points_summary',
            schema: pointsSummaryResponseSchema,
            response: pointsSummaryResponse,
          });
        }),
        map((pointsSummaryResponse) => {
          return this.transformPointsSummaryResponse(pointsSummaryResponse);
        }),
      );
  }

  private transformPointsSummaryResponse(
    pointsSummaryResponse: PointsSummaryResponse,
  ): PointsSummary {
    const { data } = pointsSummaryResponse;
    return this.transformPointsSummaryModel(data);
  }

  private transformPointsSummaryModel(
    pointsSummaryModel: PointsSummaryModel,
  ): PointsSummary {
    const { attributes } = pointsSummaryModel;
    const { pointsBalance, tranches, accruals } = attributes;

    return {
      pointsBalance,
      accruals: this.transformAccrualModels(accruals),
      tranches: this.transformTrancheModels(tranches),
    };
  }

  private transformAccrualModels(accrualsModel: AccrualModel[]): Accrual[] {
    return accrualsModel.map((accrual) => {
      return {
        ...accrual,
        transactionDate: this.dateService.parseDate(accrual.transactionDate),
      };
    });
  }

  private transformTrancheModels(tranchesModel: TrancheModel[]): Tranche[] {
    return tranchesModel.map((tranche) => {
      return {
        ...tranche,
        expiryDate: this.dateService.parseDate(tranche.expiryDate),
      };
    });
  }
}
